/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useMemo } from 'react';
import { ViewValues } from '../survey-overview-sidebar/viewOptions';
import { standards, frameworks, getGroup, GroupTypes } from '@g17eco/core';
import { Breadcrumb } from '../../types/surveyScope';
import { getSDGShortTitle } from '../../constants/sdg-data';
import { roles } from '../../constants/roles';
import { MaterialityTitle } from '../../utils/materiality';
import { BreadcrumbsRouteParams } from './breadcrumbPath';
import { UserMin } from '../../constants/users';
import { getUserFullName } from '../../constants/user';
import { NonStaticGroupConfig } from '../../model/surveyData';
import { ROUTES } from '../../constants/routes';
import { isStandardFrameworkType, isVirtualType } from '../survey-scope/virtualScope';
import { generateUrl } from '../../routes/util';
import { Materiality } from '../../types/initiative';
import { getScopeType } from '../survey-scope/scopeSelection';
import { findChildGroupByCode } from '../../utils/groups';

export const createFilterUrl = (props: BreadcrumbsRouteParams) => {
  const companyTrackerRoute =
    props.baseRoute ??
    generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
      initiativeId: props.initiativeId,
      surveyId: props.surveyId,
      page: props.page,
    });

  return [
    companyTrackerRoute,
    props.view,
    props.cardCategory,
    props.cardGroup,
    props.cardSubCategory,
    props.cardSubGroup,
    props.leafCategory,
    props.leafGroup,
    props.leafChildCategory,
    props.leafChildGroup,
  ]
    .filter(Boolean)
    .join('/');
};

const groupNameCodes = new Map<string, string>();
const groupTitles = (groupCode: string, mainView: string, breadcrumbs: Readonly<Breadcrumb[]>): string => {

  const title = groupNameCodes.get(groupCode);
  if (title) {
    return title
  }

  const [first] = breadcrumbs;
  if (!first) {
    groupNameCodes.set(groupCode, '');
    return ''
  }

  const groupType = isVirtualType(mainView) ? getScopeType(first.cardCategory) : mainView;
  const group = getGroup(groupType as GroupTypes, first.cardCategory);
  if (!group) {
    groupNameCodes.set(groupCode, '');
    return ''
  }

  const childGroup = findChildGroupByCode(group, groupCode);
  const groupName = childGroup?.name ?? '';
  groupNameCodes.set(groupCode, groupName);
  return groupName;
}

const getBreadcrumbTitle = (
  cardGroup: ViewValues | string,
  cardCategory: string,
  users: UserMin[],
  breadcrumbs: Readonly<Breadcrumb[]>,
  mainView: ViewValues | string,
  customGroups?: NonStaticGroupConfig[],
) => {
  if (isStandardFrameworkType(cardGroup as ViewValues)) {
    if (standards[cardCategory]) {
      return standards[cardCategory]?.name ?? '';
    } else if (frameworks[cardCategory]) {
      return frameworks[cardCategory]?.name ?? '';
    }
  }

  if (cardGroup === ViewValues.Group) {
    return groupTitles(cardCategory, mainView, breadcrumbs);
  }

  if (cardGroup === ViewValues.Sdg) {
    return `Goal ${cardCategory} ${(getSDGShortTitle(cardCategory))}`;
  }

  if (cardGroup === ViewValues.SdgTarget) {
    return `Target ${cardCategory} ${(getSDGShortTitle(cardCategory))}`;
  }

  if (cardGroup === ViewValues.Materiality || Object.values(Materiality).includes(cardCategory as Materiality)) {
    return MaterialityTitle[cardCategory as Materiality] ?? '';
  }

  if (cardGroup === ViewValues.Roles) {
    return roles[cardCategory]?.name ? `${roles[cardCategory].name}s` : '';
  }

  if (cardGroup === ViewValues.Users) {
    return getUserFullName(users.find(u => u._id === cardCategory));
  }

  if (customGroups) {
    return customGroups.find(g => g.groupId === cardCategory)?.groupName ?? 'Custom Group';
  }

  return '';
}

export const useBreadcrumbs = (params: BreadcrumbsRouteParams, users: UserMin[], customGroups?: NonStaticGroupConfig[]) => {
  const {
    view,
    cardCategory,
    cardGroup,
    cardSubCategory,
    cardSubGroup,
    leafCategory,
    leafGroup,
    leafChildCategory,
    leafChildGroup,
  } = params;
  return useMemo(() => {

    const breadcrumbs: Breadcrumb[] = [];

    if (!cardCategory || !cardGroup) {
      return breadcrumbs;
    }

    breadcrumbs.push({
      cardCategory: cardCategory,
      cardGroup: cardGroup as ViewValues,
      title: getBreadcrumbTitle(view, cardCategory, users, breadcrumbs, view, customGroups),
    });

    if (!cardSubCategory || !cardSubGroup) {
      return breadcrumbs;
    }

    breadcrumbs.push({
      cardCategory: cardSubCategory,
      cardGroup: cardSubGroup as ViewValues,
      title: getBreadcrumbTitle(cardGroup, cardSubCategory, users, breadcrumbs, view, customGroups),
    });

    if (leafGroup && leafCategory) {
      breadcrumbs.push({
        cardCategory: leafCategory,
        cardGroup: leafGroup as ViewValues,
        title: getBreadcrumbTitle(cardSubGroup, leafCategory, users, breadcrumbs, view, customGroups),
      });
    }

    if (leafGroup && leafChildCategory) {
      breadcrumbs.push({
        cardCategory: leafChildCategory,
        cardGroup: leafChildGroup as ViewValues,
        title: getBreadcrumbTitle(leafGroup, leafChildCategory, users, breadcrumbs, view, customGroups),
      });
    }

    return breadcrumbs;
  }, [
    cardCategory,
    cardGroup,
    view,
    users,
    customGroups,
    cardSubCategory,
    cardSubGroup,
    leafCategory,
    leafGroup,
    leafChildCategory,
    leafChildGroup,
  ]);
};
